import * as React from 'react';
import {
    Select as BaseSelect,
    SelectProps,
    selectClasses,
    SelectRootSlotProps,
} from '@mui/base/Select';
import { Option as BaseOption, optionClasses } from '@mui/base/Option';
import { styled } from '@mui/system';
import ArrowDownSvgIcon from "@/app/ui/Common/SvgComponent/ArrowDownSvgIcon";
import { text_size } from "@/app/utils/MixinSize";

const Select = React.forwardRef(function CustomSelect<
    TValue extends {},
    Multiple extends boolean,
>(props: SelectProps<TValue, Multiple>, ref: React.ForwardedRef<HTMLButtonElement>) {
    const slots: SelectProps<TValue, Multiple>['slots'] = {
        root: Button,
        listbox: Listbox,
        popup: Popup,
        ...props.slots,
    };
    return <BaseSelect {...props} ref={ref} slots={slots} />;
}) as <TValue extends {}, Multiple extends boolean>(
    props: SelectProps<TValue, Multiple> & React.RefAttributes<HTMLButtonElement>,
) => React.JSX.Element;

const Button = React.forwardRef(function Button<
    TValue extends {},
    Multiple extends boolean,
>(
    props: SelectRootSlotProps<TValue, Multiple>,
    ref: React.ForwardedRef<HTMLButtonElement>,
) {
    const { ownerState, ...other } = props;
    return (
        <StyledButton type="button" {...other} ref={ref}>
            {other.children}
            <ArrowDownSvgIcon />
        </StyledButton>
    );
} as any);

const StyledButton = styled('button', { shouldForwardProp: () => true })(
    ({ theme }) => `
  position: relative;
  ${text_size(13)}
  box-sizing: border-box;
  padding: 0px 10px;
  width: 211px;
  height: 26px;
  text-align: left;
  line-height: 14px;
  border: 1px solid #8B8B8B;
  border-radius: 4px;
  color: var(--extraPalette-text-primary);
  background-color: white;

  transition-property: all;
  transition-timing-function: cubic-bezier(0.4, 0, 0.2, 1);
  transition-duration: 120ms;

  & > svg {
    ${text_size(12)}
    position: absolute;
    height: 7px;
    width: 12px;
    top: 9.5px;
    right: 10px;
    color: #204293;
  }
  `,
);

const Listbox = styled('ul')(
    ({ theme }) => `
  ${text_size(13)}
  box-sizing: border-box;
  min-width: 211px;
  border-radius: 4px;
  overflow: auto;
  border: 1px solid #8B8B8B;
  color: var(--palette-primary-dark);  
  background: #fff;
  z-index: 999;
  max-height: 200px;
  `,
);

const Option = styled(BaseOption)(
    ({ theme }) => `
  list-style: none;
  ${text_size(13)}
  box-sizing: border-box;
  padding: 0px 10px;
  width: 100%;
  height: 26px;
  text-align: left;
  align-content: center;
  line-height: 14px;
  color: var(--palette-primary-dark);
  background-color: white;
  cursor: default;

  &:last-of-type {
    border-bottom: none;
  }

  &.${optionClasses.selected} {
    background-color: rgba(25, 118, 210, 0.12);
  }

  &.${optionClasses.highlighted} {
  }

  &:hover:not(.${optionClasses.disabled}) {
    background-color: rgba(0, 0, 0, 0.04);
  }
  `,
);

const Popup = styled('div')`
  z-index: 30;
`;

const Label = styled('label')(
    ({ theme }) => `
    ${text_size(13)}
    font-weight: 300;
    line-height: 14px;
    letter-spacing: -0.36176469922065735px;
    text-align: right;
    color: var(--palette-primary-dark);
    padding-right: 5px;
  `,
);

export { Select, Option, Label };